import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import type { Salida, SalidaContexto } from '@/lib/types';

const SALIDAS_KEY = qk('salidas');

/** Sin ID consulta la camada activa; null deshabilita la consulta. */
export function useContextoSalidas(camadaId?: number | null) {
  return useApiQuery<SalidaContexto | null>({
    queryKey: [...SALIDAS_KEY, 'contexto', camadaId ?? 'activa'],
    queryFn: () =>
      apiFetch(`/v1/camadas/salidas-contexto${camadaId != null ? `?camada_id=${camadaId}` : ''}`),
    enabled: camadaId !== null,
    staleTime: 30 * 1000,
  });
}

export interface SalidaCreateInput {
  fecha_carga: string;
  matadero: string;
  numero_camion?: string;
  numero_albaran?: string;
  lineas: Array<{ lote_nave_id: number; unidades_cargadas: number }>;
  observaciones?: string;
  kg_confirmados_destino?: number | string | null;
  fecha_recepcion_peso?: string;
}

export function useSalidasCamada(camadaId: number | null) {
  return useApiQuery<Salida[]>({
    queryKey: camadaId != null ? [...SALIDAS_KEY, camadaId] : qk('salidas', 'none'),
    queryFn: () => apiFetch<Salida[]>(`/v1/camadas/${camadaId}/salidas`),
    enabled: camadaId != null,
    staleTime: 30 * 1000,
  });
}

export function useCrearSalida(camadaId: number) {
  return useApiMutation<SalidaCreateInput, Salida>({
    mutationFn: (body, context) =>
      apiFetch<Salida>(`/v1/camadas/${camadaId}/salidas`, {
        offlineBase: context.offlineBase,
        method: 'POST',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...SALIDAS_KEY, 'contexto'],
      [...SALIDAS_KEY, camadaId],
      ['camadas', camadaId, 'detalle'],
    ],
  });
}

export interface SalidaUpdateInput extends SalidaCreateInput {
  salidaId: number;
}

export function useActualizarSalida(camadaId: number) {
  return useApiMutation<SalidaUpdateInput, Salida>({
    mutationFn: ({ salidaId, ...body }, context) =>
      apiFetch<Salida>(`/v1/camadas/${camadaId}/salidas/${salidaId}`, {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...SALIDAS_KEY, 'contexto'],
      [...SALIDAS_KEY, camadaId],
      ['camadas', camadaId, 'detalle'],
    ],
  });
}

export function useAnularSalida(camadaId: number) {
  return useApiMutation<number, void>({
    mutationFn: (salidaId, context) =>
      apiFetch<void>(`/v1/camadas/${camadaId}/salidas/${salidaId}`, {
        offlineBase: context.offlineBase,
        method: 'DELETE',
      }),
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...SALIDAS_KEY, 'contexto'],
      [...SALIDAS_KEY, camadaId],
      ['camadas', camadaId, 'detalle'],
    ],
  });
}

export function useActualizarPesoSalida(camadaId: number) {
  return useApiMutation<
    { salidaId: number; kg_confirmados_destino: number | string; fecha_recepcion_peso?: string },
    Salida
  >({
    mutationFn: ({ salidaId, kg_confirmados_destino, fecha_recepcion_peso }, context) =>
      apiFetch<Salida>(`/v1/camadas/${camadaId}/salidas/${salidaId}/peso`, {
        offlineBase: context.offlineBase,
        method: 'PATCH',
        body: JSON.stringify({ kg_confirmados_destino, fecha_recepcion_peso }),
      }),
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...SALIDAS_KEY, 'contexto'],
      [...SALIDAS_KEY, camadaId],
      ['camadas', camadaId, 'detalle'],
    ],
  });
}
